import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  CloudDownload as ExportIcon,
  Storage as DatabaseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { productService, customFieldService, Product, CustomFieldDefinition } from '../../lib/supabase';

export default function DbProducts() {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  // Custom fields
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);

  // Stats
  const [stats, setStats] = useState<{
    total: number;
    byStatus: Record<string, number>;
    byStockStatus: Record<string, number>;
    byFeedSource: Record<string, number>;
  } | null>(null);

  // Detail dialog
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    loadProducts();
    loadCustomFields();
    loadStats();
  }, [page, rowsPerPage]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const { data, count } = await productService.getAll(page + 1, rowsPerPage);
      setProducts(data || []);
      setTotalCount(count || 0);
    } catch (error: any) {
      console.error('Error loading products:', error);
      toast.error('Greška pri učitavanju: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomFields = async () => {
    try {
      const data = await customFieldService.getAll();
      setCustomFields(data || []);
    } catch (error) {
      console.error('Error loading custom fields:', error);
    }
  };

  const loadStats = async () => {
    try {
      const data = await productService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadProducts();
      return;
    }

    setLoading(true);
    try {
      const data = await productService.search(searchQuery, {
        status: statusFilter || undefined,
        stock_status: stockFilter || undefined,
        supplier: supplierFilter || undefined,
      });
      setProducts(data || []);
      setTotalCount(data?.length || 0);
    } catch (error: any) {
      toast.error('Greška pri pretrazi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getStockStatusColor = (status?: string) => {
    switch (status) {
      case 'instock': return 'success';
      case 'outofstock': return 'error';
      case 'onbackorder': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'publish': return 'success';
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'private': return 'info';
      default: return 'default';
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return '-';
    return `${price.toFixed(2)} KM`;
  };

  const openDetail = (product: Product) => {
    setSelectedProduct(product);
    setDetailOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DatabaseIcon /> DB Proizvodi
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Proizvodi iz Supabase baze podataka
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'grey.100' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="text.primary">{stats.total.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Ukupno</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'grey.100' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="text.primary">{(stats.byStockStatus?.instock || 0).toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Na stanju</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'grey.100' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="text.primary">{(stats.byStockStatus?.outofstock || 0).toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Nema na stanju</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'grey.100' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" color="text.primary">{Object.keys(stats.byFeedSource || {}).length}</Typography>
                <Typography variant="body2" color="text.secondary">Feed izvora</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search & Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pretraži po SKU ili nazivu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Svi</MenuItem>
                  <MenuItem value="publish">Objavljeno</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Na čekanju</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Zalihe</InputLabel>
                <Select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  label="Zalihe"
                >
                  <MenuItem value="">Sve</MenuItem>
                  <MenuItem value="instock">Na stanju</MenuItem>
                  <MenuItem value="outofstock">Nema</MenuItem>
                  <MenuItem value="onbackorder">Backorder</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                fullWidth
              >
                Traži
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                  setStockFilter('');
                  setSupplierFilter('');
                  loadProducts();
                  loadStats();
                }}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>SKU</TableCell>
                <TableCell>Naziv</TableCell>
                <TableCell>Brend</TableCell>
                <TableCell align="right">Cijena</TableCell>
                <TableCell align="right">Nabavna</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell>Zalihe</TableCell>
                <TableCell>Dobavljač</TableCell>
                <TableCell>Feed</TableCell>
                <TableCell align="center">Akcije</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nema proizvoda u bazi
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(product)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                        {product.sku}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {product.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                        {(product as any).brand || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatPrice(product.regular_price)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {formatPrice((product as any).nabavna_cijena)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={
                          (product.stock_quantity || 0) > 10
                            ? 'success.main'
                            : (product.stock_quantity || 0) > 0
                            ? 'warning.main'
                            : 'error.main'
                        }
                      >
                        {product.stock_quantity ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.stock_status || 'N/A'}
                        size="small"
                        color={getStockStatusColor(product.stock_status) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {(product as any).dobavljac || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.feed_source || 'manual'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Detalji">
                        <IconButton size="small" onClick={() => openDetail(product)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Po stranici:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} od ${count}`}
        />
      </Card>

      {/* Product Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span">{selectedProduct?.name}</Typography>
          <Typography variant="body2" color="text.secondary" fontFamily="monospace">
            SKU: {selectedProduct?.sku}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Grid container spacing={3}>
              {/* Lijeva strana - Slika */}
              <Grid item xs={12} sm={3}>
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {selectedProduct.images && (selectedProduct.images as any[]).length > 0 ? (
                    <img
                      src={(selectedProduct.images as any[])[0]?.src || (selectedProduct.images as any[])[0]}
                      alt={selectedProduct.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography color="text.secondary" variant="caption">Nema slike</Typography>
                  )}
                </Box>
              </Grid>

              {/* Desna strana - Podaci */}
              <Grid item xs={12} sm={9}>
                {/* Red 1: Osnovni podaci */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">EAN</Typography>
                    <Typography variant="body2">{(selectedProduct as any).ean || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Brend</Typography>
                    <Typography variant="body2">{(selectedProduct as any).brand || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                    <Chip label={selectedProduct.status} size="small" color={getStatusColor(selectedProduct.status) as any} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">Dobavljač</Typography>
                    <Typography variant="body2">{(selectedProduct as any).dobavljac || '-'}</Typography>
                  </Grid>
                </Grid>

                {/* Red 2: Cijene i količine */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">Regularna</Typography>
                      <Typography variant="body1" fontWeight="bold">{formatPrice(selectedProduct.regular_price)}</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">Akcijska</Typography>
                      <Typography variant="body1" fontWeight="bold" color="error.main">{formatPrice(selectedProduct.sale_price)}</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">Nabavna</Typography>
                      <Typography variant="body1" fontWeight="bold">{formatPrice((selectedProduct as any).nabavna_cijena)}</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">Količina</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedProduct.stock_quantity ?? '-'}</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                      <Typography variant="caption" color="text.secondary" display="block">Zalihe</Typography>
                      <Chip label={selectedProduct.stock_status} size="small" color={getStockStatusColor(selectedProduct.stock_status) as any} />
                    </Grid>
                  </Grid>
                </Paper>

                {/* Kratki opis - samo ako postoji i razlikuje se od opisa */}
                {selectedProduct.short_description &&
                 selectedProduct.short_description !== selectedProduct.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Kratki opis</Typography>
                    <Typography variant="body2" dangerouslySetInnerHTML={{ __html: selectedProduct.short_description }} />
                  </Box>
                )}
              </Grid>

              {/* Opis - puna širina */}
              {selectedProduct.description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Opis</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 150, overflow: 'auto' }}>
                    <Typography variant="body2" dangerouslySetInnerHTML={{ __html: selectedProduct.description }} />
                  </Paper>
                </Grid>
              )}

              {/* Meta podaci */}
              {selectedProduct.meta_data && Object.keys(selectedProduct.meta_data).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Meta podaci</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Grid container spacing={1}>
                      {Object.entries(selectedProduct.meta_data).map(([key, value]) => (
                        <Grid item xs={6} sm={4} md={3} key={key}>
                          <Typography variant="caption" color="text.secondary">{key}</Typography>
                          <Typography variant="body2" noWrap>{String(value)}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Feed Info */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Feed Info</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">Izvor</Typography>
                    <Typography variant="body2">{selectedProduct.feed_source || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">Zadnja sinkronizacija</Typography>
                    <Typography variant="body2">
                      {selectedProduct.last_feed_update ? new Date(selectedProduct.last_feed_update).toLocaleString() : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Hash</Typography>
                    <Typography variant="body2" fontFamily="monospace" fontSize="small" noWrap>
                      {selectedProduct.source_hash || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Zatvori</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
